import React, { useState } from "react";
import PropTypes from "prop-types";

import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import Skeleton from "@mui/material/Skeleton";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Collapse from "@mui/material/Collapse";
import { propOr, length } from "ramda";
import { styled } from "@mui/material/styles";

import Category from "~/libs/category";

import ListItemButton from "@mui/material/ListItemButton";

const ScrollableList = styled(List)(({ theme }) => ({
  "&.MuiList-root": {
    overflow: "auto"
  },
  "&:focus-visible": {
    // "--dsb-outline-color": "green",
    outline: theme.outline(),
    outlineOffset: "-2px"
  }
}));

const ListItemSkeleton = styled(Skeleton)(() => ({
  "&.MuiSkeleton-root": {
    marginBottom: 8
  }
}));

const ListItemTextSkeleton = styled(Skeleton)(() => ({
  "&.MuiSkeleton-root": {
    margin: "10px auto"
  }
}));

const getSkeletonCount = (catsort, dlsActiveTab, dlsCategories) => {
  const cprop = dlsActiveTab === "page" ? "pages" : "book";
  let count = length(propOr([], cprop, catsort));
  count += length(dlsCategories.filter(c => c.isStatic));
  return count;
};

function DlsCategoriesNode({ category, depth, currentDlsCategory, setCurrentCategory }) {
  const [isOpen, setIsOpen] = useState(false);

  function getListItemStyles(depth) {
    return { paddingLeft: depth * 16 };
  }

  function getCategoryTitle(cat) {
    const { title, count } = cat;
    if (Category.isDisabled(cat) || count <= 1) {
      return title;
    }
    return `${title} (${count})`;
  }

  function onIconClick(event) {
    event.stopPropagation();
    setIsOpen(!isOpen);
  }

  function findNextNonEmptyCategory(cat) {
    if (cat.references.length > 0 || cat.subCategories === 0) return cat;
    for (const subCat of cat.subCategories) {
      const nonEmptyCat = findNextNonEmptyCategory(subCat);
      if (nonEmptyCat) return nonEmptyCat;
    }
    return cat;
  }

  function onItemClick() {
    setIsOpen(true);
    setCurrentCategory(findNextNonEmptyCategory(category))();
  }

  const Icon = isOpen ? ExpandLessIcon : ExpandMoreIcon;

  return (
    <>
      <ListItemButton
        disabled={Category.isDisabled(category)}
        selected={Category.isInPath(category, currentDlsCategory)}
        onClick={onItemClick}
        style={getListItemStyles(depth)}
      >
        <ListItemText primary={getCategoryTitle(category)} />
        {category.subCategories.length > 0 && <Icon onClick={onIconClick} />}
      </ListItemButton>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <DlsCategoriesList
          dlsCategories={category.subCategories}
          depth={depth + 1}
          isDLSMaterialsLoading={false}
          setCurrentCategory={setCurrentCategory}
          currentDlsCategory={currentDlsCategory}
        />
      </Collapse>
    </>
  );
}

function DlsCategoriesList({
  dlsCategories,
  setCurrentCategory,
  currentDlsCategory,
  isDLSMaterialsLoading,
  dlsActiveTab,
  catsort,
  depth
}) {
  if (depth === 1 && !currentDlsCategory && dlsCategories.length > 0) {
    const firstCat = dlsCategories.find(category => !category.isStatic);
    if (firstCat) setCurrentCategory(firstCat)();
  }

  function renderDlsCategories() {
    if (!dlsCategories || dlsCategories.length === 0) return null;
    return (
      <ScrollableList>
        {dlsCategories.map(category => (
          <DlsCategoriesNode
            key={`category_${category.fullTitle}_${category.view}`}
            category={category}
            depth={depth}
            currentDlsCategory={currentDlsCategory}
            setCurrentCategory={setCurrentCategory}
          />
        ))}
      </ScrollableList>
    );
  }

  function renderSkeletons() {
    const skeletonCount = getSkeletonCount(catsort, dlsActiveTab, dlsCategories);

    return (
      <ScrollableList>
        {Array.from({ length: skeletonCount }, (_, idx) => (
          <ListItemSkeleton key={idx} variant="rectangular" animation="wave" height={45} width="100%">
            <ListItemTextSkeleton height={25} width="80%" />
          </ListItemSkeleton>
        ))}
      </ScrollableList>
    );
  }

  // return renderSkeletons();
  return isDLSMaterialsLoading ? renderSkeletons() : renderDlsCategories();
}

DlsCategoriesList.defaultProps = {
  depth: 1
};

DlsCategoriesList.propTypes = {
  dlsCategories: PropTypes.array,
  setCurrentCategory: PropTypes.func,
  currentDlsCategory: PropTypes.object,
  isDLSMaterialsLoading: PropTypes.bool.isRequired,
  dlsActiveTab: PropTypes.string,
  catsort: PropTypes.object
};

DlsCategoriesNode.propTypes = {
  category: PropTypes.object,
  depth: PropTypes.number,
  currentDlsCategory: PropTypes.object,
  setCurrentCategory: PropTypes.func
};

export default DlsCategoriesList;
